import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from 'common/toast';
import { useRole } from 'components/Hooks/UserHooks';
import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import * as Yup from "yup";
// Import React FilePond
import EditorCustom from 'components/Common/EditorCustom';
import LabelWrapper from 'components/Common/LabelWrapper';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import 'filepond/dist/filepond.min.css';
import { checkCurrentUsageNews, postCreateUsingAINews } from 'store/thunks';
import SpinnerScreen from '../../Components/SpinnerScreen';
import BoxImageSearch from 'pages/News/Components/BoxImageSearch';
import Hashtag from 'components/Common/Hashtag';
import TooltipCustom from 'components/Common/TooltipCustom';
import IconMagic from 'pages/ImageRepository/Icons/IconMagic';

export interface Tag {
    id: string;
    text: string;
}

const FILE_SIZE = 10 * 1024 * 1024; // 10MB
const SUPPORTED_FORMATS = [
    'application/pdf', // .pdf
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
];

const ModalAIArticleGeneration = ({
    isOpen,
    onCloseClick,
    onPublish,
    onSave,
    onReloadListNews,
}: any) => {

    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();

    const MAX_CHAR_COUNT = 7000;

    const fileInputRef = useRef<any>(null);
    const modalBodyInnerRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const refContentNews = useRef<any>(null);

    const [articleGeneration, setArticleGeneration] = useState<any>(null);

    const [title, setTitle] = useState<any>('');
    const [content, setContent] = useState<any>('');

    const [currentUsageNews, setCurrentUsageNews] = useState<number>(0);
    const [isOpenUseGeneration, setIsOpenUseGeneration] = useState<boolean>(false);

    const [isOpenMessCheck, setIsOpenMessCheck] = useState<boolean>(false);

    const [charCount, setCharCount] = useState(0);

    const handleSubmit = async (values: any) => {
        try {
            const res: any = await checkCurrentUsageNews();
            if (res?.data) {
                setCurrentUsageNews((prev) => Number(res?.data?.current_available || 0));
            }
            setIsOpenUseGeneration((_prev) => true);
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const onClickCloseUseGeneration = () => {
        setIsOpenUseGeneration((_prev) => false);
    };

    const onClickCloseMessCheck = () => {
        setIsOpenMessCheck((_prev) => false);
    };

    const onClickGeneration = async () => {
        try {
            setIsLoading((_prev) => true);
            onClickCloseUseGeneration();
            const values = formik?.values;
            const keywords = (values?.keywords || [])?.map((item: any) => item?.text);
            const data = {
                content: values?.content || '',
                files: values?.file || null,
                keywords: keywords?.length === 0 ? '' : keywords?.join(','),
            };
            const response: any = await postCreateUsingAINews(data);
            setIsLoading((_prev) => false);
            if (response?.code === 200) {
                const contentCheck = response?.data?.content_check?.body_matches || [];
                let contentBody = response?.data?.body || '';
                if (contentCheck?.length > 0) {
                    for (let item of contentCheck) {
                        const keyword = item?.value || '';
                        contentBody = contentBody?.replaceAll(keyword, `<span style="background-color: rgba(255, 255, 0, 0.7);" data-mce-style="background-color: rgba(255, 255, 0, 0.7);">${keyword}</span>`);
                    }
                }
                setArticleGeneration((_prev: any) => ({ ...response?.data, body: contentBody }));
                onReloadListNews && onReloadListNews();
                if (contentCheck?.length > 0) {
                    setIsOpenMessCheck((_prev) => true);
                } else {
                    toast(
                        `${t("The process has been completed.")}`,
                        CONFIG_OPTION_TOAST_NORMAL
                    );
                }
            } else {
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    }

    const validationSchema = Yup.object({
        content: Yup.string().when(['file'], (file: any, schema: any) => {
            return (!file || !file?.[0]) ? schema.required(`${t("Content is required when no file is provided")}`) : schema.nullable();
        }),
        file: Yup.mixed()
            .test(
                'fileSize',
                t('File too large. Max size is 10MB.'),
                (value: any) => !value || (value?.size <= FILE_SIZE)
            )
            .test(
                'fileFormat',
                t('Only Word and PDF files can be selected.'),
                (value: any) => !value || SUPPORTED_FORMATS.includes(value?.type)
            )
            .nullable(),
        keywords: Yup.array().of(
            Yup.object().shape({
                id: Yup.string(),
                text: Yup.string(),
            })
        ).nullable()
    });

    const formik = useFormik({
        initialValues: {
            file: null,
            content: '',
            keywords: []
        },
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
    });

    const handleChangeFile = (value: any | null) => {
        if (value?.target?.files[0] !== null || value === null) {
            formik?.setFieldValue('file', value?.currentTarget?.files[0] || null);
            if (fileInputRef?.current && value === null) {
                fileInputRef.current.value = '';
            }
        }
        formik?.setFieldValue('content', '');
    }

    const handleChangeContent = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const inputValue = event.target.value;
        const charCount = inputValue.length;

        if (charCount <= MAX_CHAR_COUNT) {
            formik.setFieldValue('content', inputValue);
            setCharCount(charCount);
        }

        if (!!formik?.values?.file) {
            formik?.setFieldValue('file', null);
            if (fileInputRef?.current) {
                fileInputRef.current.value = '';
            }
        }
    }

    const handleResetArticle = () => {
        handleChangeFile(null);
        setArticleGeneration((_prev: any) => null);
        formik?.setFieldValue('keywords', []);
        setTimeout(() => {
            formik?.setErrors({});
        }, 300);
    };

    const handleClose = () => {
        onCloseClick && onCloseClick();
        fileInputRef.current = null;
        formik?.setFieldValue('title', '');
        formik?.setFieldValue('content', '');
        formik?.setFieldValue('file', null);
        setArticleGeneration(null);
        setTimeout(() => {
            formik?.setErrors({});
        }, 500);
    };

    const handlePublish = () => {
        onPublish && onPublish({ ...articleGeneration, title: title, body: refContentNews?.current?.getContent() || '' });
    };

    const handleSave = () => {
        const dataNews = {
            id: articleGeneration?.id,
            title: title,
            content: refContentNews?.current?.getContent() || ''
        };
        onSave && onSave(true, dataNews);
    };

    const onSetImageInsert = (item: any) => {
        try {
            if (!articleGeneration?.id) {
                return;
            }
            const link_image = item?.image_url;
            const tags_image = (item?.tags || [])?.join(',') || '';
            if (!link_image || !(refContentNews as any)?.current) return;
            const editor = (refContentNews as any).current;
            const selection = editor?.selection?.getRng();
            const start = selection?.startOffset;
            const __content = `
                 <div style="text-align: center; margin: 1rem 0;">
                    <p style="text-align: center;">
                    <img src="${link_image}" alt=${tags_image} style="max-width:70%;"/>
                    </p>
                     <p style="text-align: center; color: #555;">
                        ${t('Source')}: 
                    </p>
                </div>`;
            const bookmark = editor.selection.getBookmark();
            if (bookmark) {
                editor.selection.moveToBookmark(bookmark);
                editor.insertContent(__content);
            } else {
                const content = editor.getContent();
                const updatedContent = content.replace(
                    /(<br\b[^>]*>|&nbsp;)/i,
                    `$1${__content}`
                );
                editor.setContent(updatedContent);
            }
            resetCursorToStart();
        } catch (error) {
            //
        }
    }


    const resetCursorToStart = () => {
        const editor = refContentNews?.current;
        if (editor) {
            const body = editor.getBody();
            const brNode = body.querySelector('br');
            let targetNode = null;
            if (brNode) {
                targetNode = brNode;
            } else {
                const textNodes = Array.from(body.childNodes).filter((node: any) => node.nodeType === Node.TEXT_NODE && node.nodeValue?.includes('\u00A0'));
                if (textNodes.length > 0) {
                    targetNode = textNodes[0];
                }
            }
            if (targetNode) {
                const parentNode = targetNode.parentNode;
                const offset = Array.from(parentNode.childNodes).indexOf(targetNode) + 1;
                editor.selection.setCursorLocation(parentNode, offset);
            }
            const newBookmark = editor.selection.getBookmark();
        };
    };

    useEffect(() => {
        const _title: any = `${(articleGeneration?.title || '')}`;
        const _content: any = `${(articleGeneration?.body || '')}`;
        setContent((_prev: any) => _content);
        setTitle((_prev: any) => _title);
        return () => {
            setContent((_prev: any) => '');
            setTitle((_prev: any) => '');
            if (fileInputRef?.current) {
                fileInputRef.current = null;
            }
        }
    }, [JSON.stringify(articleGeneration)]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                formik?.setErrors({});
            }, 300);
        }
    }, [isOpen]);

    useEffect(() => {
        document.title = `${t('AI Article Generation')} | ${t('PRESS Ai')}`;
        document.body.classList.remove('vertical-sidebar-enable');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    return (
        <React.Fragment>
            <Modal
                isOpen={!!isOpen}
                centered={true}
                size="xl"
                toggle={handleClose}
                keyboard={true}
                className="modal-fullscreen"
            >
                <ModalHeader toggle={handleClose} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    {t('AI Article Generation')}
                </ModalHeader>
                <ModalBody className="">
                    <form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col sm={12} lg={3} xl={3} className='mb-3 mb-lg-0'>
                                <div className="position-relative p-3 pt-4" style={{ border: '1px dashed var(--vz-primary)', borderRadius: '8px' }}>
                                    <span className="position-absolute text-primary px-2" style={{ top: 0, left: '10px', transform: 'translateY(-50%)', backgroundColor: 'var(--vz-secondary-bg)' }}>{t('Source Material')}</span>
                                    <div className="d-flex flex-column justify-content-between mb-0 box-content-ai-article-generation gap-2" style={{ height: 'calc(100dvh - 210px)', minHeight: '400px' }}>
                                        <Row>
                                            <Col sm={7}>
                                                <div>
                                                    <LabelWrapper label={t('Upload Article Material')} description={t('Only Word and PDF files can be selected.')} isShow={true}>
                                                        <div className="custom-file-button">
                                                            <Input className="form-control" accept=".pdf,.doc,.docx"
                                                                label="Upload Article Material"
                                                                data-title="Upload Article Material"
                                                                id="formSizeDefault"
                                                                type="file"
                                                                innerRef={fileInputRef}
                                                                onChange={handleChangeFile}
                                                            />
                                                            {!formik?.values?.file && <div className="text-upload">{t('No file chosen')}</div>}
                                                            {!formik?.values?.file ? <button className="btn btn-sm btn-icon-upload"> <i className="ri-upload-line" /></button> :
                                                                <button className="btn btn-sm btn-remove-file" onClick={() => handleChangeFile(null)}>
                                                                    <i className=" ri-close-fill" />
                                                                </button>}
                                                        </div>
                                                    </LabelWrapper>
                                                    {formik.touched.file && formik.errors.file ? (
                                                        <div className="text-danger">{formik.errors.file || ''}</div>
                                                    ) : null}
                                                </div>
                                            </Col>
                                            <Col sm={5}>
                                                <button className="btn btn-md w-100 btn-light" type="button" disabled={isLoading} onClick={() => handleResetArticle()}>
                                                    <i className="ri-refresh-line align-bottom me-1" /> {t('Reset Article')}
                                                </button>
                                            </Col>
                                        </Row>
                                        <LabelWrapper label={t('Enter Article Material')} isShow={true} className="flex-grow-1 mt-3">
                                            <Input
                                                className="form-control flex-grow-1 pt-3"
                                                label="Enter Article Material"
                                                type="textarea"
                                                placeholder={`${t('Please enter the article content.')} \n${t('The maximum length is 7,000 characters including spaces.')}`}
                                                value={formik?.values?.content}
                                                disabled={!!formik?.values?.file}
                                                style={{ height: '100%' }}
                                                onChange={handleChangeContent}
                                            />
                                        </LabelWrapper>
                                        <div className="text-muted text-end fs-12">
                                            <span>{t('including spaces')}</span> {charCount} / 7000
                                        </div>
                                        {formik.touched.content && formik.errors.content && !formik?.values?.file ? (
                                            <div className="text-danger">{formik.errors.content || ''}</div>
                                        ) : null}
                                        <div>
                                            <Label htmlFor="keywords" className="form-label mb-1">{t('Keyword')}   <TooltipCustom
                                                title={t('Please enter the main keywords for the article. Based on the keywords you provide, AI will generate an SEO-optimized article.')}
                                                className="d-inline-block vertical-align-middle ms-1"
                                                style={{ transform: 'translateY(1px)' }}
                                            >
                                                <i className="ri-question-line align-bottom text-secondary" ></i>
                                            </TooltipCustom></Label>
                                            <Hashtag
                                                name="keywords"
                                                placeholder={`${t("Enter a keyword and press Enter (only one allowed)")}...`}
                                                suggestions={[]}
                                                initialValue={formik?.values?.keywords || []}
                                                onChangeTag={(event: any) => {
                                                    formik.setFieldValue("keywords", event);
                                                }}
                                                idSelected="hashtags-CampaignFormMulti"
                                                maxHashtags={2000}
                                                maxHeight="150px"
                                            />
                                            {formik.touched.keywords && formik.errors.keywords ? (
                                                <div className="text-danger mt-2">{formik.errors.keywords}</div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-end gap-2 mt-3">
                                        <button
                                            className="btn btn-primary fs-14 px-1"
                                            color="light"
                                            type="submit"
                                            disabled={isLoading}
                                            style={{ width: "190px" }}
                                        >
                                            {/* <i
                                                className="bx bxs-magic-wand"
                                                style={{ lineHeight: "22px" }}
                                            /> */}
                                            <IconMagic />
                                            <span className="ms-1">{t("AI Article Generation")}</span>
                                        </button>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={12} lg={6} xl={6} className='mb-3 mb-lg-0'>
                                <div className="position-relative p-3 px-0 pt-3" style={{ border: '1px dashed var(--vz-primary)', borderRadius: '8px' }} ref={modalBodyInnerRef}>
                                    <span className="position-absolute text-primary px-2" style={{ top: 0, left: '10px', transform: 'translateY(-50%)', backgroundColor: 'var(--vz-secondary-bg)' }}>{t('Generated Article')}</span>
                                    <div className="editor-border-radius-0 p-0 position-relative d-flex flex-column justify-content-between mb-0 box-content-ai-article-generation overflow-y-auto color-scrollbar-os" style={{ height: 'calc(100dvh - 210px)', minHeight: '400px' }}>
                                        <Input
                                            name="text"
                                            type="textarea"
                                            className={`form-control h3 fs-24  px-3 mb-0 py-1`}
                                            placeholder=""
                                            rows={1}
                                            style={{ width: '100%', border: '1px solid #ececec', borderRadius: 0, borderBottom: 'none', borderTop: 'none', minHeight: '38px', fontWeight: '500', color: 'var(--vz-heading-color)' }}
                                            value={title || ''}
                                            onChange={(event: any) => {
                                                const input = event.target.value;
                                                setTitle((_prev: any) => input);
                                            }}
                                            autoComplete="off"
                                        />
                                        <div className="flex-grow-1">
                                            <EditorCustom
                                                initialValue={`${content}`}
                                                ref={refContentNews}
                                                height={'calc(100%)'}
                                                fontSize={17}
                                                isAutoCenterImageUpload={true}
                                            />
                                        </div>
                                        <SpinnerScreen isShow={isLoading || articleGeneration == null} isAnimationText={isLoading} textLoading={isLoading ? "AI is generating the article!" : "Try generating an article with AI!"} className="duration-4" style={{ borderRadius: 'var(--vz-border-radius)' }} />
                                    </div>
                                    <div className="text-end mt-3 px-3 d-flex justify-content-end align-items-end " style={{ minHeight: '48px' }}>
                                        <div>
                                            {(!!articleGeneration && !isLoading) && <>
                                                <button
                                                    className="btn btn-primary flex-grow-1 fs-14 me-3"
                                                    color="light"
                                                    type="button"
                                                    disabled={!articleGeneration || isLoading}
                                                    onClick={handleSave}
                                                >
                                                    <i
                                                        className="ri-login-circle-line"
                                                        style={{ lineHeight: "22px" }}
                                                    />
                                                    <span className="ms-1">{t("Button Save")}</span>
                                                </button>
                                            </>}
                                            <button
                                                className="btn btn-primary flex-grow-1 fs-14"
                                                color="light"
                                                type="button"
                                                disabled={!articleGeneration || isLoading}
                                                onClick={handlePublish}
                                            >
                                                <i
                                                    className="ri-share-circle-line"
                                                    style={{ lineHeight: "22px" }}
                                                />
                                                <span className="ms-1">{t("Publish")}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={12} lg={3} xl={3} className='mb-3 mb-lg-0'>
                                <div className="position-relative p-3 px-0 pt-3" style={{ border: '1px dashed var(--vz-primary)', borderRadius: '8px', height: 'calc(100dvh - 110px)', }}>
                                    <span className="position-absolute text-primary px-2" style={{ top: 0, left: '10px', transform: 'translateY(-50%)', backgroundColor: 'var(--vz-secondary-bg)' }}>{t('Image')}</span>
                                    <div className={`box-source-image overflow-y-hidden overflow-x-hidden h-100 px-0`}>
                                        <BoxImageSearch news_id={''} ref={modalBodyInnerRef} onSetImageInsert={onSetImageInsert} resetCursorToStart={resetCursorToStart} />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </form>
                </ModalBody>
            </Modal>

            <Modal
                isOpen={!!isOpenUseGeneration}
                centered={true}
                size="sm"
                toggle={onClickCloseUseGeneration}
                keyboard={true}
            >
                <ModalHeader>
                    {t('Confirm')}
                </ModalHeader>
                <ModalBody className="">
                    <div className="row g-4">
                        <Col sm={12}>
                            <div className="row g-3">
                                <div className="mt-2 text-center">
                                    <h5 className="mb-2">
                                        {`${(t('One usage is deducted.'))}`}
                                    </h5>
                                    <h6 className="mb-4 fs-15 text-muted">
                                        {`${(t('Daily available uses: _NUMBER_ times')).replace('_NUMBER_', `${currentUsageNews}`)}`}
                                    </h6>
                                    <div className="d-flex gap-3 justify-content-center">
                                        <button
                                            className="btn btn-soft-secondary fs-14"
                                            type="button"
                                            color="light"
                                            onClick={onClickCloseUseGeneration}
                                            style={{ minWidth: '100px' }}
                                        >
                                            {t("Button Close")}
                                        </button>
                                        <button
                                            className={`btn btn-primary fs-14`}
                                            type="button"
                                            onClick={onClickGeneration}
                                            style={{ minWidth: '100px' }}
                                            disabled={Number(currentUsageNews) === 0}
                                        >
                                            {t("Button OK")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </div>
                </ModalBody>
            </Modal >

            <Modal
                isOpen={isOpenMessCheck}
                centered={true}
                size="sm"
                toggle={onClickCloseMessCheck}
                keyboard={true}
            >
                <ModalHeader>
                    {t('Confirm')}
                </ModalHeader>
                <ModalBody className="">
                    <div className="row g-4">
                        <Col sm={12}>
                            <div className="row g-3">
                                <div className="mt-2 text-center">
                                    <div className="mb-3 fs-16" style={{ fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: (`${(t('Please verify time-related content in the article before use!'))}`).replaceAll('[BR]', '<br />') }}>

                                    </div>
                                    <div className="d-flex gap-3 justify-content-center">
                                        <button
                                            className={`btn btn-secondary fs-14`}
                                            type="button"
                                            onClick={onClickCloseMessCheck}
                                            style={{ minWidth: '100px' }}
                                        >
                                            {t("Button OK")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </div>
                </ModalBody>
            </Modal >

        </React.Fragment >
    );
};

export default ModalAIArticleGeneration;